import React from 'react';
import { Table, Space, Button, Popconfirm, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { EyeOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import { resendVerificationEmail } from '../UsersServices';
import { message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';


const UserTable = ({
    users,
    loading,
    pagination,
    screens,
    handleTableChange,
    showModal,
    handleDelete,
    showViewModal,
    getUserTokenCall,
}) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [resendLoading, setResendLoading] = useState({});
    const [deleteLoading, setDeleteLoading] = useState({});

    const handleResendVerificationEmail = async (id) => {
        setResendLoading(prev => ({ ...prev, [id]: true }));
        try {
            await resendVerificationEmail(id);
            message.success(t('verification_email_resent_successfully'));
        } catch (error) {
            handleError(error);
        } finally {
            setResendLoading(prev => ({ ...prev, [id]: false }));
        }
    };
    const StatusTag = ({ status, text }) => (
        <Tag color={status ? 'success' : 'error'}>
            {text}
        </Tag>
    );

    const getResponsiveColumns = () => {
        const baseColumns = [
            {
                title: t('full_name'),
                dataIndex: 'full_name',
                key: 'full_name',
                fixed: screens.xs ? 'left' : null,
                sorter: true,
                render: (text, record) => (
                    <Space direction="vertical" size={0}>
                        <span>{text}</span>
                        {!screens.sm && (
                            <span style={{ fontSize: '12px', color: '#888' }}>
                                {record.email}
                            </span>
                        )}
                    </Space>
                ),
            },
            {
                title: t('email'),
                dataIndex: 'email',
                key: 'email',
                responsive: ['sm'],
                sorter: true,
            },
            {
                title: t('role'),
                key: 'role',
                responsive: ['sm'],
                filters: [
                    { text: t('admin'), value: true },
                    { text: t('simple_user'), value: false },
                ],
                render: (_, record) => (
                    <span>{record.is_admin ? t('admin') : t('simple_user')}</span>
                ),
            },
            {
                title: t('gender'),
                dataIndex: 'gender',
                key: 'gender',
                responsive: ['sm'],
                sorter: true,
                filters: [
                    { text: 'Male', value: 'male' },
                    { text: 'Female', value: 'female' },
                ],
            },
            {
                title: t('status'),
                key: 'status',
                responsive: ['sm'],
                filters: [
                    { text: t('active'), value: 'active' },
                    { text: t('inactive'), value: 'inactive' },
                    { text: t('verified'), value: 'verified' },
                    { text: t('unverified'), value: 'unverified' },
                ],
                render: (_, record) => (
                    <Space size={4}>
                        <StatusTag status={!record.is_disabled} text={!record.is_disabled ? t('active') : t('inactive')} />
                        <StatusTag status={record.is_verified} text={record.is_verified ? t('verified') : t('unverified')} />
                    </Space>
                ),
            },
            {
                title: t('actions'),
                key: 'actions',
                fixed: screens.xs ? 'right' : null,
                width: screens.xs ? 100 : 150,
                render: (_, record) => (
                    <Space size="small">
                        <Tooltip title={t('get_token')}>
                            <Button
                                type="primary"
                                icon={<AppstoreOutlined />}
                                onClick={() => getUserTokenCall(record.id)}
                                size={'small'}
                                disabled={!record.is_verified}
                            />
                        </Tooltip>
                        <Tooltip title={t('view_user')}>
                            <Button
                                type="primary"
                                icon={<EyeOutlined />}
                                onClick={() => showViewModal(record)}
                                size={'small'}
                            />
                        </Tooltip>
                        <Tooltip title={t('edit_user')}>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => showModal(record)}
                                size={'small'}
                            />
                        </Tooltip>
                        <Popconfirm
                            title={t('delete_user')}
                            description={t('are_you_sure_you_want_to_delete_this_user')}
                            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                            onConfirm={() => handleDelete(record.id)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Tooltip title={t('delete_user')}>
                                <Button
                                    type="primary"
                                    danger
                                    icon={<DeleteOutlined />}
                                    size={'small'}
                                    loading={deleteLoading[record.id]}
                                />
                            </Tooltip>
                        </Popconfirm>
                        <Tooltip title={t('resend_verification_email')}>
                            <Button
                                type="primary"
                                icon={<MailOutlined />}
                                onClick={() => handleResendVerificationEmail(record.id)}
                                size={'small'}
                                loading={resendLoading[record.id]}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
        ];

        return baseColumns;
    };

    return (
        <Table
            columns={getResponsiveColumns()}
            dataSource={users}
            rowKey="id"
            pagination={{
                ...pagination,
                size: screens.xs ? 'small' : 'default',
                showSizeChanger: !screens.xs,
                pageSizeOptions: ['10', '25', '50', '100'],
            }}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            size={screens.xs ? 'small' : 'middle'}
        />
    );
};

export default UserTable;
